import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { VersionInfo } from '../../services/updateService';
import { UpdateService } from '../../services/updateService';

/**
 * 更新检查状态
 */
export type UpdateCheckState = 'idle' | 'checking' | 'available' | 'no-update' | 'error';

/**
 * 用户更新偏好设置
 */
export interface UpdatePreferences {
  snoozeUntil: number | null;  // 暂停提醒到指定时间戳（3天后）
}

/**
 * 更新 Modal 显示状态
 */
export const updateModalVisibleAtom = atom<boolean>(false);

/**
 * 更新检查状态
 */
export const updateCheckStateAtom = atom<UpdateCheckState>('idle');

/**
 * 当前版本信息
 */
export const currentVersionAtom = atom<string>(UpdateService.getCurrentVersion());

/**
 * 最新版本信息
 */
export const latestVersionInfoAtom = atom<VersionInfo | null>(null);

/**
 * 用户更新偏好（持久化存储）
 */
export const updatePreferencesAtom = atomWithStorage<UpdatePreferences>(
  'tempo-update-preferences',
  {
    snoozeUntil: null
  }
);

/**
 * 上次检查更新的时间戳（持久化存储）
 */
export const lastUpdateCheckAtom = atomWithStorage<number>(
  'tempo-last-update-check',
  0
);

/**
 * 计算是否应该显示更新 Modal
 */
export const shouldShowUpdateModalAtom = atom<boolean>((get) => {
  const modalVisible = get(updateModalVisibleAtom);
  const checkState = get(updateCheckStateAtom);
  const preferences = get(updatePreferencesAtom);
  const isManualCheck = get(isManualUpdateCheckAtom);

  // 如果 Modal 已经显示，直接返回
  if (modalVisible) return true;

  // 如果没有可用更新，不显示
  if (checkState !== 'available') return false;

  // 如果是手动检查，忽略暂停期
  if (isManualCheck) return true;

  // 检查是否在暂停期内
  if (preferences.snoozeUntil && Date.now() < preferences.snoozeUntil) {
    return false;
  }

  return true;
});

/**
 * 检查是否需要进行更新检查
 * 避免过于频繁的检查
 */
export const shouldCheckForUpdatesAtom = atom<boolean>((get) => {
  const preferences = get(updatePreferencesAtom);
  const lastCheck = get(lastUpdateCheckAtom);

  // 如果在暂停期内，不检查更新
  if (preferences.snoozeUntil && Date.now() < preferences.snoozeUntil) {
    return false;
  }

  // 如果距离上次检查不到1小时，不重复检查
  const oneHour = 60 * 60 * 1000;
  if (lastCheck && (Date.now() - lastCheck) < oneHour) {
    return false;
  }

  return true;
});

/**
 * 更新检查错误信息
 */
export const updateCheckErrorAtom = atom<string | null>(null);

/**
 * 是否为手动检查更新（用于忽略暂停期）
 */
export const isManualUpdateCheckAtom = atom<boolean>(false);

/**
 * 设置暂停提醒（3天）
 */
export const snoozeUpdateReminderAtom = atom(
  null,
  (get, set) => {
    const threeDays = 3 * 24 * 60 * 60 * 1000;
    const snoozeUntil = Date.now() + threeDays;

    set(updatePreferencesAtom, {
      snoozeUntil
    });

    // 隐藏 Modal
    set(updateModalVisibleAtom, false);
  }
);

/**
 * 设置短期暂停提醒（1小时）
 */
export const snoozeUpdateReminderShortAtom = atom(
  null,
  (get, set) => {
    const oneHour = 60 * 60 * 1000;
    const snoozeUntil = Date.now() + oneHour;

    set(updatePreferencesAtom, {
      snoozeUntil
    });

    // 隐藏 Modal
    set(updateModalVisibleAtom, false);
  }
);

/**
 * 重置更新状态
 */
export const resetUpdateStateAtom = atom(
  null,
  (get, set) => {
    set(updateModalVisibleAtom, false);
    set(updateCheckStateAtom, 'idle');
    set(latestVersionInfoAtom, null);
    set(updateCheckErrorAtom, null);
  }
);

/**
 * 记录更新检查时间
 */
export const recordUpdateCheckAtom = atom(
  null,
  (get, set) => {
    set(lastUpdateCheckAtom, Date.now());
  }
);
